'use client';

import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import styles from './TestimonialsSection.module.css';

interface Review {
    type: string;
    label: string;
    stars: number;
    text: React.ReactNode;
    author: string;
}

const reviews: Review[] = [
    // Column 1
    { type: 'mainland', label: 'Mainland', stars: 5, text: <>Setup my IT consultancy in Dubai Mainland. RotanaBiz handled the <strong>local service agent</strong> and bank account in record time.</>, author: 'Ahmed K., Tech Consultant' },
    { type: 'freezone', label: 'Freezone', stars: 5, text: <>The team helped me choose <strong>IFZA</strong> for my marketing agency. No visa deposit required and super fast processing.</>, author: 'Sarah L., Digital Agency' },
    { type: 'banking', label: 'Banking', stars: 5, text: <>I was rejected by 2 banks before coming to RotanaBiz. They fixed my profile and got me an account with <strong>Wio Bank</strong> in 3 days.</>, author: 'John D., E-commerce' },
    { type: 'visa', label: 'Golden Visa', stars: 5, text: <>Secured the <strong>10-Year Golden Visa</strong> for myself and my family. The team knew exactly which property documents were needed.</>, author: 'Elena R., Investor' },
    { type: 'tax', label: 'Corp Tax', stars: 5, text: <>Confused about the 9% tax? Their tax team clarified my <strong>Small Business Relief</strong> eligibility immediately. Essential service.</>, author: 'Mike T., General Trading' },
    { type: 'offshore', label: 'Offshore', stars: 4, text: <>Set up a <strong>JAFZA Offshore</strong> holding company to protect my assets. Very professional and discreet service.</>, author: 'Robert P., Wealth Holding' },
    { type: 'setup', label: 'License', stars: 5, text: <>Got my <strong>General Trading License</strong> renewed with them. Cheaper than going direct to DED because of their package deal.</>, author: 'Fatima S., Wholesaler' },

    // Column 2
    { type: 'freezone', label: 'RAKEZ', stars: 5, text: <>Moved my factory to <strong>RAKEZ</strong> industrial zone. RotanaBiz handled all the environmental clearances and lease agreements.</>, author: 'Kumar V., Manufacturing' },
    { type: 'lifestyle', label: 'Lifestyle', stars: 5, text: <>Relocated from London to Dubai. The transition was seamless. My kids are in school and I'm paying <strong>0% personal tax</strong>.</>, author: 'James B., Architect' },
    { type: 'bcenters', label: 'Office', stars: 5, text: <>Rented a private office at their <strong>Business Center</strong> in RAK. Great amenities, high-speed internet, and free coffee!</>, author: 'Maria G., Freelancer' },
    { type: 'visa', label: 'Family', stars: 5, text: <>Sponsoring my wife and 3 kids was stressful until RotanaBiz took over. They handled the <strong>medical typing and Emirates ID</strong> for everyone.</>, author: 'Tarek M., Engineer' },
    { type: 'setup', label: 'Speed', stars: 5, text: <><strong>72 hours</strong>. That's how long it took from signing the papers to having my license in hand. Incredible efficiency.</>, author: 'Lisa W., Coach' },
    { type: 'banking', label: 'Compliance', stars: 4, text: <>Helped me navigate the complex <strong>KYC requirements</strong> for my crypto trading business. Only firm that understood Web3.</>, author: 'David C., Crypto Trader' },
    { type: 'mainland', label: 'Instant', stars: 5, text: <>Got the <strong>Instant License</strong> for my coffee shop. No tenancy contract was needed for the first year. Huge cash saver.</>, author: 'Omar H., F&B Owner' },

    // Column 3
    { type: 'freezone', label: 'Meydan', stars: 5, text: <>Wanted a premium address for my consulting firm. <strong>Meydan Freezone</strong> was the perfect choice suggested by their advisor.</>, author: 'Sophie A., Luxury Consultant' },
    { type: 'tax', label: 'VAT', stars: 5, text: <>They handle my quarterly <strong>VAT filing</strong>. I never worry about fines or deadlines anymore. Peace of mind is worth every penny.</>, author: 'Rahul S., Electronics' },
    { type: 'setup', label: 'Pro', stars: 5, text: <>Their PRO service is top-notch. They pick up documents from my office and drop them back. <strong>Zero hassle</strong>.</>, author: 'Karim Z., Real Estate' },
    { type: 'lifestyle', label: 'Support', stars: 5, text: <>Always available on WhatsApp. I had a visa issue at the airport at 2 AM and they actually answered and resolved it.</>, author: 'Jennifer K., Event Planner' },
    { type: 'offshore', label: 'RAK ICC', stars: 5, text: <>Used a <strong>RAK ICC</strong> company to purchase Dubai property. Best way to manage inheritance and asset protection.</>, author: 'Sergei V., Investor' },
    { type: 'setup', label: 'Cost', stars: 5, text: <>Transparent pricing. No hidden "innovation fees" or "service charges" that other agencies add at the end.</>, author: 'Ali R., Logistics' },
];

export default function TestimonialsSection() {
    // Split reviews into 3 columns for the masonry layout
    const col1 = [...reviews.slice(0, 7), ...reviews.slice(0, 7)]; // Duplicate for infinite scroll
    const col2 = [...reviews.slice(7, 14), ...reviews.slice(7, 14)];
    const col3 = [...reviews.slice(14, 21), ...reviews.slice(14, 21)];

    return (
        <section className={styles.section}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.ratingSummary}>
                        <span className={styles.stars}>★★★★★</span>
                        <span className={styles.ratingText}>4.9/5 Average Rating</span>
                    </div>
                    <motion.h2
                        className={styles.title}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        Real Stories from <span className={styles.highlight}>Successful Founders</span>
                    </motion.h2>
                    <p className={styles.subtitle}>
                        Join thousands of entrepreneurs who trusted RotanaBiz to build their UAE legacy.
                    </p>
                </div>

                <div className={styles.wallWrapper}>
                    {/* Column 1 */}
                    <div className={`${styles.wallCol} ${styles.col1}`}>
                        <div className={styles.colTrack}>
                            {col1.map((review, i) => (
                                <ReviewCard key={`c1-${i}`} review={review} />
                            ))}
                        </div>
                    </div>

                    {/* Column 2 */}
                    <div className={`${styles.wallCol} ${styles.col2}`}>
                        <div className={styles.colTrack}>
                            {col2.map((review, i) => (
                                <ReviewCard key={`c2-${i}`} review={review} />
                            ))}
                        </div>
                    </div>

                    {/* Column 3 */}
                    <div className={`${styles.wallCol} ${styles.col3}`}>
                        <div className={styles.colTrack}>
                            {col3.map((review, i) => (
                                <ReviewCard key={`c3-${i}`} review={review} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ReviewCard({ review }: { review: Review }) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <div
            ref={cardRef}
            onMouseMove={handleMouseMove}
            className={styles.reviewCard}
        >
            <div
                className={styles.cardGlow}
                style={{ left: mousePos.x, top: mousePos.y }}
            />
            <div className={styles.cardHead}>
                <span className={`${styles.catBadge} ${review.type === 'tax' ? styles.catTax :
                    review.type === 'banking' ? styles.catBanking :
                        review.type === 'setup' ? styles.catSetup :
                            review.type === 'lifestyle' ? styles.catLifestyle :
                                review.type === 'visa' ? styles.catVisa :
                                    review.type === 'mainland' ? styles.catMainland :
                                        review.type === 'freezone' ? styles.catFreezone :
                                            review.type === 'offshore' ? styles.catOffshore :
                                                review.type === 'crypto' ? styles.catCrypto :
                                                    styles.catSetup
                    }`}>{review.label}</span>
                <span className={styles.stars}>{"★".repeat(review.stars)}</span>
            </div>
            <p className={styles.reviewText}>{review.text}</p>
            <div className={styles.author}>{review.author} <span className={styles.verified}>✔</span></div>
        </div>
    );
}
